import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { TanTable } from './TanTable';
import { formatGenset } from '../utils/reeferGenset';
import yardService from '../services/yard.service';
import { ReeferLogResponse } from '../types/yard';

interface ReeferLogPanelProps {
  pronumber: number | null;
  active?: boolean;
  refreshNonce?: number;
}

const formatDateTime = (isoString: string | null | undefined): string => {
  if (!isoString) return '';
  const date = new Date(isoString);
  return date.toLocaleString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
};

export const ReeferLogPanel: React.FC<ReeferLogPanelProps> = ({ pronumber, active = true, refreshNonce = 0 }) => {
  const [logs, setLogs] = useState<ReeferLogResponse[]>([]);
  const [loading, setLoading] = useState(false);

  const fetch = useCallback(async () => {
    if (!pronumber) return;
    setLoading(true);
    try {
      const data = await yardService.getReeferLogs({ cmspronumber: pronumber, limit: 500 });
      setLogs(data);
    } catch (err) {
      console.error('Failed to fetch reefer logs:', err);
    } finally {
      setLoading(false);
    }
  }, [pronumber]);

  useEffect(() => {
    if (active && pronumber) {
      fetch();
    }
  }, [active, pronumber, fetch, refreshNonce]);

  const columns = useMemo<ColumnDef<ReeferLogResponse>[]>(() => [
    {
      accessorKey: 'updated_at',
      header: 'Date/Time',
      size: 170,
      cell: (info) => formatDateTime(info.getValue() as string),
    },
    {
      id: 'temp',
      header: 'Temp',
      size: 90,
      accessorFn: (row) => `${row.temperature}°${row.tempscale || 'F'}`,
      cell: (info) => (
        <span className="px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
          {info.getValue() as string}
        </span>
      ),
    },
    {
      accessorKey: 'genset',
      header: 'Genset Required',
      size: 140,
      cell: (info) => formatGenset(info.getValue() as string | null, 'Not specified'),
    },
    {
      accessorKey: 'pluggedin',
      header: 'Genset Submitted',
      size: 150,
      cell: (info) => formatGenset(info.getValue() as string | null, 'Not recorded'),
    },
    {
      accessorKey: 'gensetunit',
      header: 'Genset Unit #',
      size: 130,
      cell: (info) => (info.getValue() as string) || '-',
    },
    {
      accessorKey: 'ventsetting',
      header: 'Vent Setting',
      size: 130,
      cell: (info) => (info.getValue() as string) || '-',
    },
    {
      accessorKey: 'yard_name',
      header: 'Yard',
      size: 200,
      cell: (info) => (info.getValue() as string) || '-',
    },
    {
      accessorKey: 'equipno',
      header: 'Equipment #',
      size: 140,
      cell: (info) => (info.getValue() as string) || '-',
    },
    {
      accessorKey: 'manualentry',
      header: 'Source',
      size: 100,
      cell: (info) => ((info.getValue() as string) === 'Y' ? 'Manual' : 'Auto'),
    },
    {
      accessorKey: 'updated_by',
      header: 'User',
      size: 140,
      cell: (info) => (info.getValue() as string) || '-',
    },
  ], []);

  return (
    <div className="h-full flex flex-col">
      <div className="flex-1 min-h-[200px]">
        <TanTable
          data={logs}
          columns={columns}
          loading={loading}
          cardTitle="Reefer Log"
          searchPlaceholder="Search reefer logs..."
          fillContainer
          enableSearch
          enableReset
          enableShowHideCols
          enableColumnFilters
          enableZebraStriping={false}
          defaultSorting={[{ id: 'updated_at', desc: true }]}
        />
      </div>
    </div>
  );
};

export default ReeferLogPanel;
